import React from 'react';
import {graphql, useMutation} from 'react-relay/hooks';
import classNames from 'classnames';
import BarChart01Icon from '../../../../assets/icons0/bar-chart-01.svg';
import {getRecaptchaToken} from '../../../../utils/recaptcha';

const ViewCounter = React.memo(({postId, viewsCount, isAuthorized, postRef, viewedByCurrentUser}) => {
    const hasTrackedView = React.useRef(false);

    const [incrementView, isIncrementingView] = useMutation(
        graphql`
            mutation viewCounterIncrementViewMutation($request: IncrementViewRequestInput!) {
                incrementView(request: $request) {
                    id
                    viewsCount
                    viewedByCurrentUser
                }
            }
        `
    );

    React.useEffect(() => {
        // Skip if already viewed by current user or not authorized
        if (!isAuthorized || viewedByCurrentUser || hasTrackedView.current || !postRef?.current) {
            return;
        }

        const observer = new IntersectionObserver(
            async (entries) => {
                entries.forEach(async (entry) => {
                    if (entry.isIntersecting && !hasTrackedView.current) {
                        hasTrackedView.current = true;
                        try {
                            const reCaptcha = await getRecaptchaToken();
                            incrementView({
                                variables: {request: {postId: postId, reCaptcha}},
                                onError: () => {
                                    // Reset on error so we can retry
                                    hasTrackedView.current = false;
                                }
                            });
                        } catch (error) {
                            console.error('reCAPTCHA error:', error);
                            hasTrackedView.current = false;
                        }
                        // Disconnect observer after tracking view
                        observer.disconnect();
                    }
                });
            },
            {
                // Trigger when 100% of the post is visible
                threshold: 1.0,
                // Shrink detection area slightly to trigger a bit later (post must be more fully visible)
                rootMargin: '0px'
            }
        );

        observer.observe(postRef.current);

        return () => {
            observer.disconnect();
        };
    }, [postId, isAuthorized, incrementView, postRef, viewedByCurrentUser]);

    return (
        <div className='display-flex align-items-center column-gap-0dot5rem padding-bottom-0dot25rem border-bottom-style-solid border-bottom-width-0dot125rem border-bottom-color-transparent'>
            <BarChart01Icon 
                className={classNames('display-block width-1dot25rem height-1dot25rem', {
                    'color-blue-gray-500': viewedByCurrentUser,
                    'color-gray-400': !viewedByCurrentUser
                })}
            />
            <span className={classNames('text-sm', {
                'color-blue-gray-500 semibold': viewedByCurrentUser,
                'color-gray-500': !viewedByCurrentUser
            })}>
                {(viewsCount || 0)}{isIncrementingView ? '...' : ''}
            </span>
        </div>
    );
});

export default ViewCounter;

